import {Shape} from 'diagram-js/lib/model';
import {PathwaysViewerCommands} from '@ispm/shared/bpmn-io/pathways-viewer-commands';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {ShapeFocus} from '@ispm/shared/bpmn-io/services/shape-focus';
import {END_NODE_ID, HEAD_NODE_ID} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import Selection from 'diagram-js/lib/features/selection/Selection';

export class TreeNavigator {

  private currentNeighborhoodHeadNodeId: any;

  constructor(private pathwaysViewerCommands: PathwaysViewerCommands, private canvas, private graphicsFactory, private elementRegistry, private shapeFocus: ShapeFocus,
              private config, private selection: Selection) {
  }

  private _currentLayer: string = HEAD_NODE_ID;
  private _layerIsTriggered: string = HEAD_NODE_ID; //this needed for overlay calculation since its calculated before navigate to other layer

  get currentLayer(): string {
    return this._currentLayer;
  }

  set currentLayer(value: string) {
    this._currentLayer = value;
  }

  get layerIsTriggered(): string {
    return this._layerIsTriggered;
  }

  set layerIsTriggered(value: string) {
    this._layerIsTriggered = value;
  }

  public getCurrentNeighborhoodHeadNodeId() {
    return this.currentNeighborhoodHeadNodeId ? this.currentNeighborhoodHeadNodeId : null;
  }
  public navigateToSubTree(neighborhoodEntryNode: Shape) {
    if (neighborhoodEntryNode.outgoing && neighborhoodEntryNode.outgoing.length > 0 && neighborhoodEntryNode.outgoing[0]) {
      const neighborhoodHeadNode = neighborhoodEntryNode.outgoing[0].target;
      const layer = neighborhoodHeadNode.id;
      this.layerIsTriggered = layer;
      this.navigateToSubTreeByHead(neighborhoodHeadNode);
      this.hideFloatingNodesOnPreviousLayer();
      this.currentLayer = layer;
      this.displayFloatingNodesOnCurrentLayer();
    }
  }

  private navigateToSubTreeByHead(neighborhoodHeadNode: Shape, isFocused?: boolean) {
    this.currentNeighborhoodHeadNodeId = neighborhoodHeadNode.id;
    const rootElement = this.elementRegistry.get(HEAD_NODE_ID);
    this.hideMainTree(rootElement);
    this.displayElementsStartingFrom(neighborhoodHeadNode);
    if (isFocused !== false) {
      if (this.config.verticalLayoutMode) {
        this.shapeFocus.setFocusOnShapeTopCenter(neighborhoodHeadNode);
      } else {
        this.shapeFocus.setFocusOnShapeLeftCenter(neighborhoodHeadNode);
      }
    }
  }

  public navigateToMainTreeByHeadAndFocusOnRoot(neighborhoodHeadNode: Shape) {
    const rootElement = this.navigateToMainTreeByHead(neighborhoodHeadNode);
    if (this.config.verticalLayoutMode) {
      this.shapeFocus.setFocusOnShapeTopCenter(rootElement);
    } else {
      this.shapeFocus.setFocusOnShapeLeftCenter(rootElement);
    }
  }

  public navigateToMainTreeByHeadAndFocusOnNeiEntry(neighborhoodHeadNode: Shape) {
    this.navigateToMainTreeByHead(neighborhoodHeadNode);
    if (this.config.verticalLayoutMode) {
      this.shapeFocus.setFocusOnShapeTopCenter(ModelingUtil.getNodeParents(neighborhoodHeadNode)[0]);
    } else {
      this.shapeFocus.setFocusOnShapeRightCenter(ModelingUtil.getNodeParents(neighborhoodHeadNode)[0]);
    }
  }

  public navigateToMainTree() {
    if (this.currentNeighborhoodHeadNodeId) {
      this.navigateToMainTreeByHeadAndFocusOnRoot(this.getCurrentNeighborhoodHeadNode());
      this.hideFloatingNodesOnPreviousLayer();
      this.setCurrentLayerMainTree();
      this.displayFloatingNodesOnCurrentLayer();

    }
  }

  private getCurrentNeighborhoodHeadNode(): Shape {
    if (this.getCurrentNeighborhoodHeadNodeId()) {
      return this.elementRegistry.get(this.getCurrentNeighborhoodHeadNodeId());
    }
    return null;
  }

  public toggleNextElementsVisibility(element, hide: boolean): void {
    if (hide === false) {
      this.pathwaysViewerCommands.renderElement(element);
    }
    this.pathwaysViewerCommands.setElementVisibility(element, hide);
    this.markElementIfSelectedAndLazyLoaded(element, hide);

    const outgoingConnections = element.outgoing;
    let target;
    for (const conn of outgoingConnections) {
      target = conn.target;
      if (target.id !== END_NODE_ID) {
        if (hide === false) {
          this.pathwaysViewerCommands.renderElement(conn);
        }
        this.pathwaysViewerCommands.setElementVisibility(conn, hide);
        this.toggleNextElementsVisibility(target, hide);
      }
    }
  }

  public displayElementsStartingFrom(element: Shape) {
    this.toggleNextElementsVisibility(element, false);
  }

  public hideElementsStartingFrom(element: Shape) {
    this.toggleNextElementsVisibility(element, true);
  }

  public navigatedToNeighborhood(): boolean {
    return this.currentLayer !== HEAD_NODE_ID;
  }

  public currentLayerIsMainTree(): boolean {
    return this.currentLayer === HEAD_NODE_ID;
  }

  private setCurrentLayerMainTree() {
    this.currentLayer = HEAD_NODE_ID;
  }

  private navigateToMainTreeByHead(neighborhoodHeadNode: Shape) {
    this.currentNeighborhoodHeadNodeId = null;
    const rootElement = this.elementRegistry.get(HEAD_NODE_ID);
    this.layerIsTriggered = HEAD_NODE_ID;
    this.displayMainTree(rootElement);
    this.hideElementsStartingFrom(neighborhoodHeadNode);
    this.hideFloatingNodesOnPreviousLayer();
    this.setCurrentLayerMainTree();
    this.displayFloatingNodesOnCurrentLayer();

    return rootElement;
  }

  private toggleMainTree(element: Shape, hide: boolean) {
    const nodeDetails = ModelingUtil.getNodeDetails(element);
    this.pathwaysViewerCommands.setElementVisibility(element, hide);
    if (nodeDetails && nodeDetails.nodeType === NodeType.neighborhood_entry) {
      return;
    }
    const outgoingConnections = element.outgoing;
    let source;
    for (const conn of outgoingConnections) {
      source = conn.target;
      if (source.id !== END_NODE_ID) {
        this.pathwaysViewerCommands.setElementVisibility(conn, hide);
        this.toggleMainTree(source, hide);
      }
    }
  }

  private hideMainTree(element: Shape) {
    this.toggleMainTree(element, true);
  }

  private displayMainTree(element: Shape) {
    this.toggleMainTree(element, false);
  }

  private markElementIfSelectedAndLazyLoaded(element, hide) {
    if (this.selection.isSelected(element) && hide === false && !this.canvas.hasMarker(element, 'selected')) {
      this.canvas.addMarker(element, 'selected');
    }
  }

  private displayFloatingNodesOnCurrentLayer() {
    this.pathwaysViewerCommands.toggleFloatingNodes(this.currentLayer, false);
  }

  private hideFloatingNodesOnPreviousLayer() {
    this.pathwaysViewerCommands.toggleFloatingNodes(this.currentLayer, true);
  }

  public getVerticalLayoutMode() {
    return this.config.verticalLayoutMode;
  }

}
